import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import { 
  Search, 
  Shield, 
  Key, 
  CheckCircle, 
  XCircle, 
  Clock, 
  User,
  RefreshCw,
  Copy,
  Check
} from 'lucide-react';

export default function JWTAuthMonitoring() {
  const [users, setUsers] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [jwtMetadata, setJwtMetadata] = useState(null);
  const [copiedField, setCopiedField] = useState('');
  const { user: currentUser } = useAuth();

  const loadJWTMetadata = async () => {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (user) {
        setJwtMetadata({
          userId: user?.id,
          email: user?.email,
          role: user?.user_metadata?.role || user?.app_metadata?.role || 'Not Set',
          tenantId: user?.user_metadata?.tenant_id || user?.app_metadata?.tenant_id || 'Not Set',
          createdAt: user?.created_at,
          lastSignIn: user?.last_sign_in_at,
          emailConfirmed: user?.email_confirmed_at ? true : false,
          userMetadata: user?.user_metadata,
          appMetadata: user?.app_metadata
        });
      }
    } catch (err) {
      console.error('Error loading JWT metadata:', err);
    }
  };

  const loadAllData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load users with their profiles
      const { data: usersData, error: usersError } = await supabase?.from('user_profiles')?.select('*')?.order('created_at', { ascending: false });

      if (usersError) throw usersError;

      // Load permissions
      const { data: permsData, error: permsError } = await supabase?.from('permissions')?.select(`
          *,
          user_profile:user_profiles(email, first_name, last_name)
        `)?.order('created_at', { ascending: false });

      if (permsError) throw permsError;

      setUsers(usersData || []);
      setPermissions(permsData || []);

      // Load current user's JWT metadata
      await loadJWTMetadata();
    } catch (err) {
      setError(err?.message);
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAllData();
    const interval = setInterval(loadAllData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const copyToClipboard = (text, field) => {
    navigator.clipboard?.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(''), 2000);
  };

  const openUserDetails = (user) => {
    setSelectedUser(user);
    setShowDetailsModal(true);
  };

  const closeDetailsModal = () => {
    setSelectedUser(null);
    setShowDetailsModal(false);
  };

  const filteredUsers = users?.filter((user) => {
    const searchLower = searchQuery?.toLowerCase();
    return (
      user?.email?.toLowerCase()?.includes(searchLower) ||
      user?.first_name?.toLowerCase()?.includes(searchLower) ||
      user?.last_name?.toLowerCase()?.includes(searchLower) ||
      user?.role?.toLowerCase()?.includes(searchLower)
    );
  });

  const getUserPermissions = (userId) => {
    return permissions?.filter(perm => perm?.user_id === userId) || [];
  };

  const getRoleColor = (role) => {
    const colors = {
      'super_admin': 'bg-purple-100 text-purple-800 border-purple-200',
      'sub_account_admin': 'bg-blue-100 text-blue-800 border-blue-200',
      'business_admin': 'bg-green-100 text-green-800 border-green-200',
      'staff': 'bg-gray-100 text-gray-800 border-gray-200',
      'customer': 'bg-orange-100 text-orange-800 border-orange-200'
    };
    return colors?.[role] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  if (loading && !users?.length) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading JWT Authentication System...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto w-full p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                <Shield className="w-8 h-8 text-blue-600" />
                JWT Authentication Monitor
              </h1>
              <p className="text-gray-600">
                Real-time monitoring of JWT-based authentication, roles, and permission assignments
              </p>
            </div>
            <Button 
              onClick={loadAllData} 
              disabled={loading}
              className="flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
            <XCircle className="w-5 h-5 text-red-600" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Current User JWT Info */}
        {jwtMetadata && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200 p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Key className="w-5 h-5 text-blue-600" />
              Your Current JWT Session
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <p className="text-xs text-gray-500 mb-1">Email</p>
                <p className="text-sm font-medium text-gray-900 flex items-center justify-between">
                  <span>{jwtMetadata?.email}</span>
                  <button
                    onClick={() => copyToClipboard(jwtMetadata?.email, 'email')}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    {copiedField === 'email' ? (
                      <Check className="w-4 h-4 text-green-600" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </button>
                </p>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <p className="text-xs text-gray-500 mb-1">Role</p>
                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(jwtMetadata?.role)}`}>
                  {jwtMetadata?.role?.toUpperCase()}
                </span>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <p className="text-xs text-gray-500 mb-1">Tenant ID</p>
                <p className="text-sm font-medium text-gray-900 flex items-center justify-between">
                  <span className="truncate">{jwtMetadata?.tenantId?.toString()?.substring(0, 8)}...</span>
                  <button
                    onClick={() => copyToClipboard(jwtMetadata?.tenantId, 'tenantId')}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    {copiedField === 'tenantId' ? (
                      <Check className="w-4 h-4 text-green-600" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </button>
                </p>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <p className="text-xs text-gray-500 mb-1">Email Verified</p>
                <div className="flex items-center gap-2">
                  {jwtMetadata?.emailConfirmed ? (
                    <>
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-medium text-green-700">Verified</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="w-4 h-4 text-red-600" />
                      <span className="text-sm font-medium text-red-700">Not Verified</span>
                    </>
                  )}
                </div>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <p className="text-xs text-gray-500 mb-1">Last Sign In</p>
                <p className="text-sm font-medium text-gray-900">
                  {jwtMetadata?.lastSignIn
                    ? new Date(jwtMetadata.lastSignIn)?.toLocaleString()
                    : 'Never'}
                </p>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <p className="text-xs text-gray-500 mb-1">Sync Status</p>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                  <span className="text-sm font-medium text-green-700">Synced</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Search Bar */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              type="text"
              placeholder="Search by email, name, or role..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="pl-10 w-full"
            />
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Users</p>
                <p className="text-2xl font-bold text-gray-900">{users?.length || 0}</p>
              </div>
              <User className="w-10 h-10 text-blue-600" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Users</p>
                <p className="text-2xl font-bold text-gray-900">
                  {users?.filter(u => u?.is_active)?.length || 0}
                </p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Permissions</p>
                <p className="text-2xl font-bold text-gray-900">{permissions?.length || 0}</p>
              </div>
              <Shield className="w-10 h-10 text-purple-600" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Last Sync</p>
                <p className="text-sm font-medium text-gray-900 flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {new Date()?.toLocaleTimeString()}
                </p>
              </div>
              <RefreshCw className="w-10 h-10 text-orange-600" />
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">User Roles & Permissions</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Permissions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Login
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers?.length > 0 ? (
                  filteredUsers?.map((user) => {
                    const userPerms = getUserPermissions(user?.id);
                    return (
                      <tr
                        key={user?.id}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => openUserDetails(user)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="h-10 w-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold">
                              {user?.first_name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase()}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {user?.first_name && user?.last_name
                                  ? `${user?.first_name} ${user?.last_name}`
                                  : user?.email}
                              </div>
                              <div className="text-sm text-gray-500">{user?.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${getRoleColor(user?.role)}`}>
                            {user?.role?.replace('_', ' ')?.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {user?.is_active ? (
                            <div className="flex items-center gap-1 text-green-700">
                              <CheckCircle className="w-4 h-4" />
                              <span className="text-sm font-medium">Active</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1 text-red-700">
                              <XCircle className="w-4 h-4" />
                              <span className="text-sm font-medium">Inactive</span>
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <Shield className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-900 font-medium">
                              {userPerms?.length} assigned
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {user?.last_login_at
                            ? new Date(user?.last_login_at)?.toLocaleString()
                            : 'Never'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Button
                            size="sm"
                            onClick={(e) => {
                              e?.stopPropagation();
                              openUserDetails(user);
                            }}
                            className="text-xs"
                          >
                            View Details
                          </Button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                      {searchQuery ? 'No users match your search' : 'No users found'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* User Details Modal */}
        {showDetailsModal && selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Shield className="w-6 h-6 text-blue-600" />
                  JWT & Permission Details
                </h2>
                <button
                  onClick={closeDetailsModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* User Profile Info */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Profile Information
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500">User ID</p>
                      <p className="text-sm font-medium text-gray-900 font-mono">{selectedUser?.id}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Email</p>
                      <p className="text-sm font-medium text-gray-900">{selectedUser?.email}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Role (JWT)</p>
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(selectedUser?.role)}`}>
                        {selectedUser?.role?.toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Tenant ID</p>
                      <p className="text-sm font-medium text-gray-900 font-mono">
                        {selectedUser?.tenant_id?.substring(0, 8)}...
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Status</p>
                      <div className="flex items-center gap-1">
                        {selectedUser?.is_active ? (
                          <>
                            <CheckCircle className="w-4 h-4 text-green-600" />
                            <span className="text-sm font-medium text-green-700">Active</span>
                          </>
                        ) : (
                          <>
                            <XCircle className="w-4 h-4 text-red-600" />
                            <span className="text-sm font-medium text-red-700">Inactive</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Created</p>
                      <p className="text-sm font-medium text-gray-900">
                        {new Date(selectedUser?.created_at)?.toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Assigned Permissions */}
                <div className="border rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Key className="w-4 h-4" />
                    Assigned Permissions
                  </h3>
                  {getUserPermissions(selectedUser?.id)?.length > 0 ? (
                    <div className="space-y-2">
                      {getUserPermissions(selectedUser?.id)?.map((perm) => (
                        <div
                          key={perm?.id}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                        >
                          <div>
                            <p className="text-sm font-medium text-gray-900">{perm?.feature}</p>
                            <p className="text-xs text-gray-500">
                              Access Level: {perm?.access_level}
                            </p>
                          </div>
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-4">
                      No permissions assigned
                    </p>
                  )}
                </div>

                {/* JWT Sync Status */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-green-900 mb-2 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    JWT Synchronization Status
                  </h3>
                  <p className="text-sm text-green-800">
                    User role and metadata are successfully synchronized with JWT tokens.
                    All authentication checks are functioning correctly.
                  </p>
                  <div className="mt-3 flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                    <span className="text-xs text-green-700 font-medium">Real-time sync active</span>
                  </div>
                </div>
              </div>

              <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4">
                <Button onClick={closeDetailsModal} className="w-full">
                  Close
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}